import { createLogger } from '../logging/index.js';

const logger = createLogger('search-env');

export interface SearchEnvOverrides {
  readonly rrfK: number | undefined;
  readonly vectorWeight: number | undefined;
  readonly candidateMultiplier: number | undefined;
  readonly queryExpansion: boolean | undefined;
}

export interface EffectiveSearchParams {
  readonly rrfK: number;
  readonly vectorWeight: number;
  readonly candidateMultiplier: number;
}

/**
 * Resolve effective search params by merging env overrides with provided defaults.
 * Used by benchmark fingerprinting to record truth, not assumptions.
 */
export function resolveEffectiveSearchParams(
  overrides: SearchEnvOverrides,
  defaults: { rrfK: number; vectorWeight: number; candidateMultiplier: number }
): EffectiveSearchParams {
  return {
    rrfK: overrides.rrfK ?? defaults.rrfK,
    vectorWeight: overrides.vectorWeight ?? defaults.vectorWeight,
    candidateMultiplier: overrides.candidateMultiplier ?? defaults.candidateMultiplier,
  };
}

/**
 * Parse and validate BK_RRF_K, BK_RRF_VECTOR_WEIGHT, BK_CANDIDATE_MULTIPLIER
 * from environment variables.
 *
 * @param strict - true: throw on invalid values (benchmark flow).
 *                 false: warn + return undefined for that field (app runtime).
 */
export function parseSearchEnvOverrides(strict: boolean): SearchEnvOverrides {
  return {
    rrfK: parseRrfK(process.env['BK_RRF_K'], strict),
    vectorWeight: parseVectorWeight(process.env['BK_RRF_VECTOR_WEIGHT'], strict),
    candidateMultiplier: parseCandidateMultiplier(process.env['BK_CANDIDATE_MULTIPLIER'], strict),
    queryExpansion: parseBoolFlag(process.env['BK_QUERY_EXPANSION'], 'BK_QUERY_EXPANSION', strict),
  };
}

function parseRrfK(raw: string | undefined, strict: boolean): number | undefined {
  if (raw === undefined || raw === '') return undefined;
  const parsed = Number.parseInt(raw, 10);
  if (Number.isNaN(parsed) || parsed <= 0) {
    const msg = `BK_RRF_K must be a positive integer, got: "${raw}"`;
    if (strict) throw new Error(msg);
    logger.warn(msg);
    return undefined;
  }
  return parsed;
}

function parseVectorWeight(raw: string | undefined, strict: boolean): number | undefined {
  if (raw === undefined || raw === '') return undefined;
  const parsed = Number.parseFloat(raw);
  if (Number.isNaN(parsed) || parsed < 0 || parsed > 1) {
    const msg = `BK_RRF_VECTOR_WEIGHT must be a float between 0 and 1, got: "${raw}"`;
    if (strict) throw new Error(msg);
    logger.warn(msg);
    return undefined;
  }
  return parsed;
}

function parseCandidateMultiplier(raw: string | undefined, strict: boolean): number | undefined {
  if (raw === undefined || raw === '') return undefined;
  const parsed = Number.parseInt(raw, 10);
  if (Number.isNaN(parsed) || parsed < 1) {
    const msg = `BK_CANDIDATE_MULTIPLIER must be an integer >= 1, got: "${raw}"`;
    if (strict) throw new Error(msg);
    logger.warn(msg);
    return undefined;
  }
  return parsed;
}

function parseBoolFlag(
  raw: string | undefined,
  name: string,
  strict: boolean
): boolean | undefined {
  if (raw === undefined || raw === '') return undefined;
  if (raw === '1') return true;
  if (raw === '0') return false;
  const msg = `${name} must be '0' or '1', got: "${raw}"`;
  if (strict) throw new Error(msg);
  logger.warn(msg);
  return undefined;
}
